<template>
    <layout-with-side-panel class="contact-view">
        <template #header>
            <a @click="onContactClose" class="contact-view__header" data-router="client-route" href="#">
                <vx-icon name="arrow-left-bold" :size="24" color="primary-dark"></vx-icon>
                <span class="contact-view__header-title">Contact View</span>
            </a>
        </template>
        <template #aside>
            <contact-info
                :contact="contact"
                :segments="segments"
                :subscriptions="subscriptions"
                :is-editing="isEditing"
                @contact:edit="onContactEdit"
            ></contact-info>
        </template>
        <template #main>
            <vx-tabs
                class="contact-view__tabs vx-scroll vx-scroll--horizontal"
                color="primary-dark"
                :tabs="tabs"
                :size="14"
                selected="messages"
                theme="v2-short"
            >
                <template #default="{ selectedTabIndex }">
                    <div class="contact-view__tabs-content_wrapper vx-scroll">
                        <div v-show="selectedTabIndex === 'messages'" data-tab-title="Messages">
                            <contact-messages :contact="contact" />
                        </div>
                        <div v-show="selectedTabIndex === 'campaigns'" data-tab-title="Campaigns">
                            <contact-campaigns :contact="contact" />
                        </div>
                        <div v-show="selectedTabIndex === 'custom_attributes'" data-tab-title="Custom Attributes">
                            <contact-custom-attributes :contact="contact" />
                        </div>
                        <div v-if="FEATURES.promotions" v-show="selectedTabIndex === 'promotions'" data-tab-title="Promotions">
                            <contact-promotions :contact="contact" data-test="promotions-section" />
                        </div>
                        <div v-show="selectedTabIndex === 'subscriptions'" data-tab-title="Subscriptions">
                            <contact-subscriptions :subscriptions="subscriptions" data-test="subscriptions-section" />
                        </div>
                    </div>
                </template>
            </vx-tabs>
        </template>
    </layout-with-side-panel>
</template>

<script setup>
import { inject, computed, ref, onMounted } from 'vue';
import LayoutWithSidePanel from '../../layouts/LayoutWithSidePanel.vue';
import { VxIcon, VxTabs } from '@voxie/frontend-components-v3';
import ContactInfo from './ContactInfo.vue';
import ContactMessages from './ContactMessages.vue';
import ContactCampaigns from './ContactCampaigns.vue';
import ContactCustomAttributes from './ContactCustomAttributes.vue';
import ContactPromotions from './ContactPromotions.vue';
import ContactSubscriptions from './ContactSubscriptions.vue';
import { contactsClient } from '../../../services';
import { useToasted } from '../../../composables/useToasted';

const props = defineProps({
    contact: {
        type: Object,
        required: true,
    },
    segments: {
        type: Array,
        required: true,
    },
});

const $toasted = useToasted();

const FEATURES = inject('FEATURES');

const isEditing = ref(false);
const isLoadingSubscriptions = ref(false);
const subscriptions = ref([]);

Bus.$on('ContactFormCreated', () => {
    isEditing.value = false;
    onContactClose();
});

const tabs = computed(() => {
    const tabs = [{ title: 'Messages', value: 'messages' }, { title: 'Campaigns', value: 'campaigns' }, { title: 'Custom Attributes', value: 'custom_attributes' }];

    if (FEATURES.promotions) {
        tabs.push({ title: 'Promotions', value: 'promotions' });
    }

    tabs.push({ title: 'Subscriptions', value: 'subscriptions' });

    return tabs;
});

const onContactClose = () => {
    Bus.$emit('contactClose');
};

const onContactEdit = () => {
    isEditing.value = true;
    Bus.$emit('contactEdit', props.contact.id);
};

const loadSubscriptions = () => {
    isLoadingSubscriptions.value = true;

    contactsClient.getContactSubscriptionStatus(Spark.state.currentTeam.id, props.contact.id).then(({ data }) => {
        subscriptions.value = data.data;

        const hasMarketingSubscription = !!subscriptions.value.find(subscription => subscription.type === 'marketing');
        const hasTransactionalSubscription = !!subscriptions.value.find(subscription => subscription.type === 'transactional');

        if (!hasMarketingSubscription) {
            subscriptions.value.push({
                type: 'marketing',
                status: 'unknown',
                reason_text: '-',
                updated_at: props.contact.created_at
            });
        }

        if (!hasTransactionalSubscription) {
            subscriptions.value.push({
                type: 'transactional',
                status: 'unknown',
                reason_text: '-',
                updated_at: props.contact.created_at
            });
        }
    }).catch(() => {
        $toasted.global.platform_error();
    }).finally(() => {
        isLoadingSubscriptions.value = false;
    });
};

onMounted(() => {
    loadSubscriptions();
})
</script>

<style lang="scss">
.contact-view {
    .c-chat__divider {
        font-family: var(--font-dm-sans);
        font-size: 0.8125rem; // 13px;
    }
}

.contact-view__header {
    display: flex;
    align-items: center;
    line-height: 40px;
    margin-bottom: 0;
    width: fit-content;

    &:hover {
        text-decoration: none;
    }
}

.contact-view__header-title {
    font-family: var(--font-dm-sans);
    font-weight: var(--weight-bold);
    font-size: 2rem;
    color: var(--color-primary-dark);
    margin-left: 18px;
}

.contact-view__tabs {
    .vx-tabs-header {
        display: flex;
        justify-content: flex-start;
        gap: 24px;
        line-height: 1;

        .vx-tab--active {
            border-bottom: 2px solid var(--color-primary-dark);
        }
    }

    .vx-tab--theme-v2-short {
        padding: 0;
        padding-bottom: 12px;
        margin: 0;
        white-space: nowrap;
    }
}

.contact-view__tabs-content_wrapper {
    margin-top: 24px;
}

.contact-view__spinner {
    display: flex;
    justify-content: center;
}

.contact-view__load-more {
    margin: 24px auto;
}

.contact-view__no-data {
    font-family: var(--font-open-sans);
    font-size: 0.9375rem; // 15px;
    font-weight: var(--weight-semibold);
    color: var(--color-grey);
}
</style>
